import React, { useEffect, useId, useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import tagsService from "services/tags.service";
import { getControlClassName } from "components/fields/FormField";
import Checkbox from "components/fields/Checkbox";
import Button from "components/button/Button";
import Loader from "components/loader/Loader";
import EmptyState from "components/states/EmptyState";
import TagBadge from "components/tags/TagBadge";

/**
 * Multi-select tag filter: a select-styled trigger that opens a checklist of
 * active tags. Selected tags are listed under the trigger as removable badges.
 *
 * @param {object} props
 * @param {Array<string|number>} [props.selectedTagIds=[]]
 * @param {(ids: Array<string|number>) => void} props.onChange
 * @param {string} [props.className] Wrapper classes (width).
 */
const TagFilter = ({ selectedTagIds = [], onChange, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    const loadTags = async () => {
      try {
        setLoading(true);
        const response = await tagsService.getTags({ isActive: true });
        setAvailableTags(response.data || []);
      } catch (error) {
        console.error("Error loading tags:", error);
      } finally {
        setLoading(false);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleToggleTag = (tagId) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];
    onChange(next);
  };

  const selectedTags = availableTags.filter((tag) => selectedTagIds.includes(tag.id));
  const triggerLabel =
    selectedTags.length === 0
      ? "All Tags"
      : `${selectedTags.length} ${selectedTags.length === 1 ? "Tag" : "Tags"}`;

  return (
    <div className={`relative ${className}`.trim()} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        aria-label={`Filter by tags: ${triggerLabel}`}
        onClick={() => setIsOpen((open) => !open)}
        className={getControlClassName({
          className: "flex cursor-pointer items-center justify-between gap-2 pr-3 text-left",
        })}
      >
        <span className="truncate">{triggerLabel}</span>
        <MdKeyboardArrowDown
          aria-hidden="true"
          className={`h-5 w-5 shrink-0 text-fg-muted transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          id={panelId}
          className="absolute z-30 mt-2 w-full min-w-[250px] overflow-hidden rounded-card border border-line bg-surface shadow-overlay"
        >
          {loading ? (
            <Loader size="sm" variant="inline" className="w-full justify-center p-4" label="Loading tags" />
          ) : availableTags.length > 0 ? (
            <>
              <ul className="max-h-64 overflow-y-auto p-2">
                {availableTags.map((tag) => (
                  <li
                    key={tag.id}
                    className="rounded-control px-3 py-2 transition-colors hover:bg-surface-subtle"
                  >
                    <Checkbox
                      checked={selectedTagIds.includes(tag.id)}
                      onChange={() => handleToggleTag(tag.id)}
                      className="items-center"
                      label={
                        <span className="flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                          {tag.type === "predefined" && (
                            <span className="text-xs font-normal text-fg-muted">Predefined</span>
                          )}
                        </span>
                      }
                    />
                  </li>
                ))}
              </ul>

              {selectedTags.length > 0 && (
                <div className="border-t border-line bg-surface-raised px-3 py-2">
                  <Button variant="ghost" size="sm" onClick={() => onChange([])}>
                    Clear All
                  </Button>
                </div>
              )}
            </>
          ) : (
            <EmptyState variant="inline" title="No tags available" />
          )}
        </div>
      )}

      {selectedTags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedTags.map((tag) => (
            <TagBadge
              key={tag.id}
              tag={tag}
              size="sm"
              onRemove={() => handleToggleTag(tag.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TagFilter;
